import React from 'react';

/**
 * Custom Miami Car Queens icon set.
 *
 * Single-weight line drawings on a 24×24 grid, stroke only, round caps and joins —
 * the same drawing language as the gold bow motif. They replace the generic library
 * icons so the page has one consistent hand.
 *
 * All of them inherit `currentColor`, so color is controlled with text utilities.
 */

interface IconProps {
  className?: string;
  strokeWidth?: number;
}
function Svg({
  className = 'h-5 w-5',
  strokeWidth = 1.5,
  children
}: IconProps & {
  children: React.ReactNode;
}) {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" className={className} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" data-id="element-154" data-mp="393hrqnhsvi8p">
      {children}
    </svg>;
}

/** VIP service */
export function IconCrown(props: IconProps) {
  return <Svg {...props} data-id="element-155" data-mp="y8axqsena8n1">
      <path d="M3.2 7.4 6 13l3-7.2 3 5.2 3-5.2L18 13l2.8-5.6-1.4 10.2H4.6Z" data-id="element-156" data-mp="y7ssx7dx2m3i" />
      <path d="M4.6 20.2h14.8" data-id="element-157" data-mp="109m4j3r7nfqh" />
    </Svg>;
}

/** Financing options */
export function IconWallet(props: IconProps) {
  return <Svg {...props} data-id="element-158" data-mp="w6xxn1m24iw5">
      <path d="M3.5 8.2h17v10.3a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" data-id="element-159" data-mp="r9z34hyhe9xf" />
      <path d="M3.5 8.2 6 4.4a1.4 1.4 0 0 1 1.2-.6h9.6a1.4 1.4 0 0 1 1.2.6l2.5 3.8" data-id="element-160" data-mp="rc23nvx9flge" />
      <path d="M15.6 13.6h3.4" data-id="element-161" data-mp="mamkk9en7p0n" />
    </Svg>;
}

/** New · Used · Leasing */
export function IconCar(props: IconProps) {
  return <Svg {...props} data-id="element-162" data-mp="cl30j0f71p47">
      <path d="M4.2 15.4 5.9 9.9A2.2 2.2 0 0 1 8 8.4h8a2.2 2.2 0 0 1 2.1 1.5l1.7 5.5" data-id="element-163" data-mp="ckt7t52rl3ut" />
      <path d="M4.2 15.4h15.6v3.1h-2.5v-1.4H6.7v1.4H4.2Z" data-id="element-164" data-mp="8j7wvp9sizn9" />
      <path d="M7.4 12.5h9.2" data-id="element-165" data-mp="8hdd4ea99dkr" />
    </Svg>;
}

/** Trade-in / price match */
export function IconSwap(props: IconProps) {
  return <Svg {...props} data-id="element-166" data-mp="3uqekujor9iiz">
      <path d="M4 9h13.5l-3-3" data-id="element-167" data-mp="3uqxvrgfzn6ni" />
      <path d="M20 15H6.5l3 3" data-id="element-168" data-mp="3upwg0yo11vzg" />
    </Svg>;
}

/** Step 01 — tell us what you need */
export function IconChat(props: IconProps) {
  return <Svg {...props} data-id="element-169" data-mp="3g48tx7y9khdy">
      <path d="M4 6.4a1.6 1.6 0 0 1 1.6-1.6h12.8A1.6 1.6 0 0 1 20 6.4v8a1.6 1.6 0 0 1-1.6 1.6H9.2L5 19.6V16h-1.6" data-id="element-170" data-mp="3g2xrpckgvhoy" />
      <path d="M8 9.4h8M8 12.2h5" data-id="element-171" data-mp="3l3v7xjlx21nt" />
    </Svg>;
}

/** Step 02 — explore your options */
export function IconCompare(props: IconProps) {
  return <Svg {...props} data-id="element-172" data-mp="3b4wbczefdwh6">
      <path d="M11 4.6v14.8" data-id="element-173" data-mp="3b44cnfpj7imo" />
      <path d="M4.4 8.2h13.2" data-id="element-174" data-mp="3b5edudrol6xj" />
      <path d="M4.4 8.2 2.6 13a2.6 2.6 0 0 0 3.6 0Z" data-id="element-175" data-mp="3b6pgubdayanl" />
      <path d="M17.6 8.2 15.8 13a2.6 2.6 0 0 0 3.6 0Z" data-id="element-176" data-mp="2qdvibi8tcn0" />
      <path d="M7.6 19.4h6.8" data-id="element-177" data-mp="7o5vlst1k52a" />
    </Svg>;
}

/** Step 03 — pick up your keys */
export function IconKey(props: IconProps) {
  return <Svg {...props} data-id="element-178" data-mp="3q13pbhz8q1o0">
      <circle cx="8.2" cy="8.4" r="3.6" data-id="element-179" data-mp="3q1wq82bipwy8" />
      <path d="M10.8 11 19 19.2" data-id="element-180" data-mp="3u3icufdutplm" />
      <path d="M16.4 16.6l1.8-1.8M14 14.2l1.8-1.8" data-id="element-181" data-mp="3u2flk6cmdl56" />
    </Svg>;
}

/** Kia factory warranty */
export function IconShield(props: IconProps) {
  return <Svg {...props} data-id="element-182" data-mp="qendf2cc5g8v">
      <path d="M12 3.4 19 6v5.2c0 4.4-2.9 7.6-7 9.4-4.1-1.8-7-5-7-9.4V6Z" data-id="element-183" data-mp="qf6s8a6ovoio" />
      <path d="m9.2 11.8 2 2 3.6-3.8" data-id="element-184" data-mp="2b52il81fu0bl" />
    </Svg>;
}
export function IconWhatsApp(props: IconProps) {
  return <Svg {...props} data-id="element-185" data-mp="266ivh4b3yihn">
      <path d="M20 11.6a8 8 0 0 1-11.9 7L4 20l1.4-4A8 8 0 1 1 20 11.6Z" data-id="element-186" data-mp="264y39lc9nbmy" />
      <path d="M9.3 8.6c.2-.4.4-.4.7-.4h.5c.2 0 .4 0 .6.5l.7 1.6c.1.2 0 .4-.1.6l-.4.5c-.1.2-.2.3 0 .6a6 6 0 0 0 2.6 2.2c.3.1.4 0 .6-.1l.5-.6c.2-.2.3-.2.6-.1l1.5.8c.3.1.4.3.4.5a1.7 1.7 0 0 1-1.7 1.6 7 7 0 0 1-5.9-5.7 2.7 2.7 0 0 1 .4-2Z" data-id="element-187" data-mp="2kvf0stls5hek" />
    </Svg>;
}
export function IconArrowRight(props: IconProps) {
  return <Svg {...props} data-id="element-188" data-mp="1n7wqhech078g">
      <path d="M4.5 12h14" data-id="element-189" data-mp="1n8odi0s04wn4" />
      <path d="m13.5 6.8 5.2 5.2-5.2 5.2" data-id="element-190" data-mp="1n9ho5cu306aj" />
    </Svg>;
}
export function IconCheck(props: IconProps) {
  return <Svg {...props} data-id="element-191" data-mp="1x5kpigh400wf">
      <path d="m4.8 12.6 4.6 4.6L19.2 6.8" data-id="element-192" data-mp="1x51hpsl6asw4" />
    </Svg>;
}
export function IconClose(props: IconProps) {
  return <Svg {...props} data-id="element-193" data-mp="3di84nkuguwss">
      <path d="M6 6l12 12M18 6 6 18" data-id="element-194" data-mp="3dhq266h7nmcf" />
    </Svg>;
}
export function IconPlus(props: IconProps) {
  return <Svg {...props} data-id="element-195" data-mp="38mkfv6jwc5nt">
      <path d="M12 5v14M5 12h14" data-id="element-196" data-mp="1i3m8trwhcr21" />
    </Svg>;
}
export function IconMinus(props: IconProps) {
  return <Svg {...props} data-id="element-197" data-mp="1n25zu6pxkt19">
      <path d="M5 12h14" data-id="element-198" data-mp="1n3h0hxo2xko5" />
    </Svg>;
}
export function IconAlert(props: IconProps) {
  return <Svg {...props} data-id="element-199" data-mp="1r2zjup095nov">
      <path d="M12 4.4 21 19.6H3Z" data-id="element-200" data-mp="1r2hhdamzyd8i" />
      <path d="M12 10v4.2M12 17h.01" data-id="element-201" data-mp="1w3448q4a72kd" />
    </Svg>;
}
export function IconInstagram(props: IconProps) {
  return <Svg {...props} data-id="element-202" data-mp="212qgaw8dicrl">
      <rect x="4" y="4" width="16" height="16" rx="4.6" data-id="element-203" data-mp="2115mj8tvuxus" />
      <circle cx="12" cy="12" r="3.6" data-id="element-204" data-mp="261bhoyzd32hm" />
      <path d="M16.6 7.4h.01" data-id="element-205" data-mp="2609ujw52mpjj" />
    </Svg>;
}
export function IconImage(props: IconProps) {
  return <Svg {...props} data-id="element-206" data-mp="2atbbe9ou8z5r">
      <rect x="3.6" y="5" width="16.8" height="14" rx="2" data-id="element-207" data-mp="2asthhjpnex10" />
      <path d="m4.6 16.4 4.2-4.2 3.4 3.4 2.6-2.6 4.6 4.6" data-id="element-208" data-mp="2etv9bfh8k6sy" />
      <circle cx="15.4" cy="9.4" r="1.2" data-id="element-209" data-mp="2evg1iyg2vdnn" />
    </Svg>;
}